import React, { useMemo } from 'react';
import { View, Text, StyleSheet, useWindowDimensions } from 'react-native';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { glassAccentBorderStyle, androidFlatSurfaceStyle } from '../../shared/theme/sharedStyles';
import { useTranslation, formatNumber } from '../../shared/i18n';
import type { MacroTotals } from '../../data/domains/menu';

const REF_WIDTH = 390;

export type DateSelectionMacroBarLayout = ReturnType<typeof buildDateSelectionMacroBarMetrics>;

export function buildDateSelectionMacroBarMetrics(window_width: number, font_scale: number) {
  const ratio = window_width / REF_WIDTH;
  const scale = Math.min(1.06, Math.max(0.86, ratio)) / Math.max(font_scale, 1);
  const ss = (size: number) => Math.round(size * scale);
  const sf = (size: number) => Math.round(size * scale * 10) / 10;

  const pad_h = ss(10);
  const pad_v = ss(10);
  const float_gap = ss(4);
  const macro_value_font = sf(14);
  const macro_label_font = sf(12);
  const calorie_value_font = sf(18);
  const calorie_label_font = sf(12);

  return {
    pad_h,
    pad_v,
    float_gap,
    bar_radius: ss(16),
    calorie_chip_radius: ss(10),
    calorie_chip_pad_h: ss(10),
    calorie_chip_pad_v: ss(5),
    calorie_chip_gap: ss(4),
    macro_value_font,
    macro_value_line: sf(17),
    macro_label_font,
    macro_label_line: sf(14),
    calorie_value_font,
    calorie_value_line: sf(22),
    calorie_label_font,
    calorie_label_line: sf(14),
    bar_height: pad_v * 2 + Math.max(ss(28), sf(22)),
  };
}

function createDateSelectionMacroSummaryBarStyles(c: ThemeColors, m: DateSelectionMacroBarLayout) {
  return StyleSheet.create({
    floatHost: {
      width: '100%',
      paddingHorizontal: appTheme.spacing.lg,
      marginBottom: m.float_gap,
      backgroundColor: 'transparent',
    },
    barShell: {
      width: '100%',
      borderRadius: m.bar_radius,
      overflow: 'hidden',
      backgroundColor: c.surface,
      ...glassAccentBorderStyle((c.borderSubtle ?? c.borderLight ?? c.border) as string, { borderRadius: m.bar_radius }),
      ...androidFlatSurfaceStyle(),
      ...appTheme.shadows.xs,
    },
    barContent: {
      paddingVertical: m.pad_v,
      paddingHorizontal: m.pad_h,
      paddingEnd: m.pad_h * 2,
    },
    bottomMacroRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      width: '100%',
    },
    bottomMacroItem: {
      alignItems: 'center',
      minWidth: 0,
      flexShrink: 1,
    },
    bottomMacroItemCalories: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.calorieUltra,
      paddingHorizontal: m.calorie_chip_pad_h,
      paddingVertical: m.calorie_chip_pad_v,
      borderRadius: m.calorie_chip_radius,
      gap: m.calorie_chip_gap,
      flexShrink: 0,
    },
    bottomMacroValueProtein: {
      fontSize: m.macro_value_font,
      lineHeight: m.macro_value_line,
      fontWeight: '700',
      color: c.protein,
    },
    bottomMacroValueCarbs: {
      fontSize: m.macro_value_font,
      lineHeight: m.macro_value_line,
      fontWeight: '700',
      color: c.carbs,
    },
    bottomMacroValueFats: {
      fontSize: m.macro_value_font,
      lineHeight: m.macro_value_line,
      fontWeight: '700',
      color: c.fats,
    },
    bottomMacroLabel: {
      fontSize: m.macro_label_font,
      lineHeight: m.macro_label_line,
      color: c.textSecondary,
      marginTop: 2,
      textAlign: 'center',
    },
    bottomMacroValueCalories: {
      fontSize: m.calorie_value_font,
      lineHeight: m.calorie_value_line,
      fontWeight: '700',
      color: c.calorie,
    },
    bottomMacroLabelCalories: {
      fontSize: m.calorie_label_font,
      lineHeight: m.calorie_label_line,
      fontWeight: '600',
      color: c.textSecondary,
    },
  });
}

type DateSelectionMacroSummaryBarProps = {
  macroTotals: MacroTotals;
};

export function DateSelectionMacroSummaryBar({ macroTotals }: DateSelectionMacroSummaryBarProps) {
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const { width: window_width, fontScale: font_scale } = useWindowDimensions();
  const metrics = useMemo(() => buildDateSelectionMacroBarMetrics(window_width, font_scale), [window_width, font_scale]);
  const styles = useMemo(() => createDateSelectionMacroSummaryBarStyles(colors, metrics), [colors, metrics]);

  return (
    <View style={styles.floatHost} pointerEvents="box-none">
      <View style={styles.barShell} accessibilityRole="summary">
        <View style={styles.barContent} pointerEvents="box-none">
          <View style={styles.bottomMacroRow}>
            <View style={styles.bottomMacroItemCalories}>
              <Text style={styles.bottomMacroValueCalories} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.85}>
                {formatNumber(macroTotals.calories)}
              </Text>
              <Text style={styles.bottomMacroLabelCalories} numberOfLines={1}>
                {t('menu.kcalLabel')}
              </Text>
            </View>

            <View style={styles.bottomMacroItem}>
              <Text style={styles.bottomMacroValueProtein} numberOfLines={1}>
                {formatNumber(macroTotals.protein)}g
              </Text>
              <Text style={styles.bottomMacroLabel} numberOfLines={1}>
                {t('dashboard.protein')}
              </Text>
            </View>

            <View style={styles.bottomMacroItem}>
              <Text style={styles.bottomMacroValueCarbs} numberOfLines={1}>
                {formatNumber(macroTotals.carbs)}g
              </Text>
              <Text style={styles.bottomMacroLabel} numberOfLines={1}>
                {t('dashboard.carbs')}
              </Text>
            </View>

            <View style={styles.bottomMacroItem}>
              <Text style={styles.bottomMacroValueFats} numberOfLines={1}>
                {formatNumber(macroTotals.fats)}g
              </Text>
              <Text style={styles.bottomMacroLabel} numberOfLines={1}>
                {t('dashboard.fat')}
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
}

/** Reserve space in the scroll area so list content clears the floating macro bar + footer buttons. */
export function getDateSelectionMacroScrollClearance(
  window_width: number,
  font_scale: number,
  show_macro: boolean,
  footer_block_height: number,
) {
  const m = buildDateSelectionMacroBarMetrics(window_width, font_scale);
  if (!show_macro) {
    return footer_block_height + appTheme.spacing.sm;
  }
  return m.bar_height + m.float_gap + footer_block_height;
}
